import React, { useState, useEffect, useCallback } from 'react';
import { useLocalization } from '../../../../hooks/useLocalization';

interface ScrapedHotelContent {
    _id: string;
    hotelId: string;
    giataId?: string;
    sourceUrl: string;
    hotelName: string;
    descriptions: {
        original: string;
        translated?: {
            sv?: string;
            en?: string;
            tr?: string;
        };
        manualEdit?: string;
    };
    roomTypes: Array<{
        name: string;
        description?: string;
        amenities?: string[];
        images?: string[];
        translated?: {
            sv?: { name: string; description?: string };
            en?: { name: string; description?: string };
        };
    }>;
    images: Array<{
        url: string;
        alt?: string;
        category?: string;
        isMain?: boolean;
    }>;
    amenities: string[];
    contactInfo: {
        phone?: string;
        email?: string;
        website?: string;
        address?: string;
    };
    scrapedAt: string;
    translationStatus: 'pending' | 'in_progress' | 'completed' | 'failed';
    isApproved: boolean;
    approvedBy?: string;
    approvedAt?: string;
    lastScrapedAt: string;
    scrapeErrors?: string[];
}

interface ScrapedHotelsManagerProps {
    theme?: 'light' | 'dark';
}

const ScrapedHotelsManager: React.FC<ScrapedHotelsManagerProps> = ({ theme = 'dark' }) => {
    const { t, language } = useLocalization();
    const isDark = theme === 'dark';

    const [hotels, setHotels] = useState<ScrapedHotelContent[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedHotel, setSelectedHotel] = useState<ScrapedHotelContent | null>(null);
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [approvalFilter, setApprovalFilter] = useState<string>('all');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [scrapeUrl, setScrapeUrl] = useState('');
    const [scrapeHotelId, setScrapeHotelId] = useState('');
    const [isScraping, setIsScraping] = useState(false);
    const [scrapeResult, setScrapeResult] = useState<{ success: boolean; message: string } | null>(null);

    // Fetch scraped hotels
    const fetchHotels = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({
                page: currentPage.toString(),
                limit: '20',
            });

            if (statusFilter !== 'all') {
                params.append('translationStatus', statusFilter);
            }
            if (approvalFilter !== 'all') {
                params.append('isApproved', approvalFilter);
            }
            if (searchTerm) {
                params.append('search', searchTerm);
            }

            const response = await fetch(`/api/hotel-scraper?${params.toString()}`);
            const data = await response.json();

            if (data.success) {
                setHotels(data.hotels || []);
                setTotalPages(data.pagination?.totalPages || 1);
            } else {
                setError(data.error || 'Failed to fetch scraped hotels');
            }
        } catch (err) {
            setError('Network error while fetching scraped hotels');
            console.error('Fetch error:', err);
        } finally {
            setLoading(false);
        }
    }, [currentPage, statusFilter, approvalFilter, searchTerm]);

    useEffect(() => {
        fetchHotels();
    }, [fetchHotels]);

    // Scrape a new hotel
    const handleScrape = async () => {
        if (!scrapeUrl || !scrapeHotelId) {
            setScrapeResult({ success: false, message: 'Please enter both Hotel ID and URL' });
            return;
        }

        setIsScraping(true);
        setScrapeResult(null);

        try {
            const response = await fetch('/api/hotel-scraper/scrape', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    hotelId: scrapeHotelId,
                    sourceUrl: scrapeUrl,
                }),
            });

            const data = await response.json();

            if (data.success) {
                setScrapeResult({ success: true, message: 'Hotel scraped successfully!' });
                setScrapeUrl('');
                setScrapeHotelId('');
                fetchHotels();
            } else {
                setScrapeResult({ success: false, message: data.error || 'Scraping failed' });
            }
        } catch (err) {
            setScrapeResult({ success: false, message: 'Network error during scraping' });
        } finally {
            setIsScraping(false);
        }
    };

    // Approve hotel content
    const handleApprove = async (hotelId: string) => {
        try {
            const response = await fetch(`/api/hotel-scraper/${hotelId}/approve`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
            });

            const data = await response.json();

            if (data.success) {
                fetchHotels();
            }
        } catch (err) {
            console.error('Approval error:', err);
        }
    };

    // Trigger translation
    const handleTranslate = async (hotelId: string) => {
        try {
            const response = await fetch(`/api/hotel-scraper/${hotelId}/translate`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
            });

            const data = await response.json();

            if (data.success) {
                fetchHotels();
            }
        } catch (err) {
            console.error('Translation error:', err);
        }
    };

    // Delete scraped content
    const handleDelete = async (hotelId: string) => {
        if (!confirm('Are you sure you want to delete this scraped content?')) return;

        try {
            const response = await fetch(`/api/hotel-scraper/${hotelId}`, {
                method: 'DELETE',
            });

            const data = await response.json();

            if (data.success) {
                fetchHotels();
            }
        } catch (err) {
            console.error('Delete error:', err);
        }
    };

    const getStatusBadge = (status: string) => {
        const statusClasses: Record<string, string> = {
            pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
            in_progress: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
            completed: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
            failed: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
        };

        return statusClasses[status] || 'bg-gray-100 text-gray-800';
    };

    return (
        <div className={`h-full flex flex-col ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
            {/* Header */}
            <div className={`flex-shrink-0 p-6 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                    <div>
                        <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {language === 'sv' ? 'Skrapade hotellbeskrivningar' : 'Scraped Hotel Content'}
                        </h1>
                        <p className={`mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                            {language === 'sv'
                                ? 'Hantera automatiskt hämtat innehåll från hotellwebbplatser'
                                : 'Manage automatically fetched content from hotel websites'}
                        </p>
                    </div>

                    {/* Quick Stats */}
                    <div className="flex gap-4">
                        <div className={`px-4 py-2 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-white'} border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                            <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Total</p>
                            <p className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{hotels.length}</p>
                        </div>
                        <div className={`px-4 py-2 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-white'} border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                            <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Approved</p>
                            <p className="text-xl font-bold text-green-500">{hotels.filter(h => h.isApproved).length}</p>
                        </div>
                        <div className={`px-4 py-2 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-white'} border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                            <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Pending</p>
                            <p className="text-xl font-bold text-yellow-500">{hotels.filter(h => h.translationStatus === 'pending').length}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Scrape New Hotel Section */}
            <div className={`flex-shrink-0 p-6 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                <div className={`p-4 rounded-xl ${isDark ? 'bg-gray-800' : 'bg-white'} border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                    <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {language === 'sv' ? 'Skrapa nytt hotell' : 'Scrape New Hotel'}
                    </h3>
                    <div className="flex flex-col md:flex-row gap-4">
                        <input
                            type="text"
                            placeholder={language === 'sv' ? 'Hotell-ID' : 'Hotel ID'}
                            value={scrapeHotelId}
                            onChange={(e) => setScrapeHotelId(e.target.value)}
                            className={`flex-1 px-4 py-2 rounded-lg border ${isDark
                                ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                                : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-500'
                                } focus:ring-2 focus:ring-teal-500 focus:border-transparent`}
                        />
                        <input
                            type="url"
                            placeholder={language === 'sv' ? 'Hotellwebbplats URL' : 'Hotel Website URL'}
                            value={scrapeUrl}
                            onChange={(e) => setScrapeUrl(e.target.value)}
                            className={`flex-[2] px-4 py-2 rounded-lg border ${isDark
                                ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                                : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-500'
                                } focus:ring-2 focus:ring-teal-500 focus:border-transparent`}
                        />
                        <button
                            onClick={handleScrape}
                            disabled={isScraping}
                            className={`px-6 py-2 rounded-lg font-medium flex items-center gap-2 ${isScraping
                                    ? 'bg-gray-400 cursor-not-allowed'
                                    : 'bg-teal-600 hover:bg-teal-700'
                                } text-white transition-colors`}
                        >
                            {isScraping ? (
                                <>
                                    <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></span>
                                    {language === 'sv' ? 'Skrapar...' : 'Scraping...'}
                                </>
                            ) : (
                                <>
                                    <span className="material-icons-round text-lg">download</span>
                                    {language === 'sv' ? 'Skrapa' : 'Scrape'}
                                </>
                            )}
                        </button>
                    </div>
                    {scrapeResult && (
                        <div className={`mt-3 px-4 py-2 rounded-lg ${scrapeResult.success
                                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                                : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                            }`}>
                            {scrapeResult.message}
                        </div>
                    )}
                </div>
            </div>

            {/* Filters */}
            <div className={`flex-shrink-0 p-6 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                <div className="flex flex-col md:flex-row gap-4">
                    <div className="flex-1">
                        <input
                            type="text"
                            placeholder={language === 'sv' ? 'Sök efter hotellnamn...' : 'Search by hotel name...'}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className={`w-full px-4 py-2 rounded-lg border ${isDark
                                ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-400'
                                : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                                } focus:ring-2 focus:ring-teal-500 focus:border-transparent`}
                        />
                    </div>
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className={`px-4 py-2 rounded-lg border ${isDark
                            ? 'bg-gray-800 border-gray-700 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                            }`}
                    >
                        <option value="all">{language === 'sv' ? 'Alla statusar' : 'All Statuses'}</option>
                        <option value="pending">{language === 'sv' ? 'Väntar' : 'Pending'}</option>
                        <option value="in_progress">{language === 'sv' ? 'Pågår' : 'In Progress'}</option>
                        <option value="completed">{language === 'sv' ? 'Klar' : 'Completed'}</option>
                        <option value="failed">{language === 'sv' ? 'Misslyckad' : 'Failed'}</option>
                    </select>
                    <select
                        value={approvalFilter}
                        onChange={(e) => setApprovalFilter(e.target.value)}
                        className={`px-4 py-2 rounded-lg border ${isDark
                            ? 'bg-gray-800 border-gray-700 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                            }`}
                    >
                        <option value="all">{language === 'sv' ? 'Alla godkännanden' : 'All Approvals'}</option>
                        <option value="true">{language === 'sv' ? 'Godkänd' : 'Approved'}</option>
                        <option value="false">{language === 'sv' ? 'Ej godkänd' : 'Not Approved'}</option>
                    </select>
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
                {loading ? (
                    <div className="flex items-center justify-center h-64">
                        <div className="animate-spin rounded-full h-12 w-12 border-4 border-teal-500 border-t-transparent"></div>
                    </div>
                ) : error ? (
                    <div className="flex items-center justify-center h-64">
                        <div className={`text-center ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                            <span className="material-icons-round text-5xl text-red-400 mb-4">error_outline</span>
                            <p>{error}</p>
                            <button
                                onClick={fetchHotels}
                                className="mt-4 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
                            >
                                {language === 'sv' ? 'Försök igen' : 'Try Again'}
                            </button>
                        </div>
                    </div>
                ) : hotels.length === 0 ? (
                    <div className="flex items-center justify-center h-64">
                        <div className={`text-center ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                            <span className="material-icons-round text-5xl mb-4">inbox</span>
                            <p>{language === 'sv' ? 'Inga skrapade hotell hittades' : 'No scraped hotels found'}</p>
                            <p className="text-sm mt-2">
                                {language === 'sv'
                                    ? 'Använd formuläret ovan för att skrapa ett nytt hotell'
                                    : 'Use the form above to scrape a new hotel'}
                            </p>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {hotels.map((hotel) => (
                            <div
                                key={hotel._id}
                                className={`p-4 rounded-xl ${isDark ? 'bg-gray-800' : 'bg-white'} border ${isDark ? 'border-gray-700' : 'border-gray-200'} hover:border-teal-500/50 transition-colors`}
                            >
                                <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                                    {/* Hotel Image */}
                                    <div className="flex-shrink-0">
                                        {hotel.images && hotel.images.length > 0 ? (
                                            <img
                                                src={hotel.images[0].url}
                                                alt={hotel.hotelName}
                                                className="w-24 h-24 rounded-lg object-cover"
                                            />
                                        ) : (
                                            <div className={`w-24 h-24 rounded-lg flex items-center justify-center ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}>
                                                <span className="material-icons-round text-3xl text-gray-400">hotel</span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Hotel Info */}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-start gap-2">
                                            <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'} truncate`}>
                                                {hotel.hotelName}
                                            </h3>
                                            {hotel.isApproved && (
                                                <span className="flex-shrink-0 px-2 py-0.5 text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 rounded-full">
                                                    Approved
                                                </span>
                                            )}
                                        </div>
                                        <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'} mt-1`}>
                                            ID: {hotel.hotelId} {hotel.giataId && `• GIATA: ${hotel.giataId}`}
                                        </p>
                                        <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-500'} mt-1 truncate`}>
                                            {hotel.sourceUrl}
                                        </p>

                                        {/* Stats */}
                                        <div className="flex flex-wrap gap-3 mt-3">
                                            <span className={`text-xs px-2 py-1 rounded ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
                                                <span className="material-icons-round text-xs align-middle mr-1">image</span>
                                                {hotel.images?.length || 0} images
                                            </span>
                                            <span className={`text-xs px-2 py-1 rounded ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
                                                <span className="material-icons-round text-xs align-middle mr-1">hotel</span>
                                                {hotel.roomTypes?.length || 0} rooms
                                            </span>
                                            <span className={`text-xs px-2 py-1 rounded ${getStatusBadge(hotel.translationStatus)}`}>
                                                {hotel.translationStatus}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Actions */}
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => {
                                                setSelectedHotel(hotel);
                                                setIsDetailModalOpen(true);
                                            }}
                                            className={`p-2 rounded-lg ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'} transition-colors`}
                                            title="View Details"
                                        >
                                            <span className="material-icons-round text-teal-500">visibility</span>
                                        </button>
                                        <button
                                            onClick={() => handleTranslate(hotel._id)}
                                            className={`p-2 rounded-lg ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'} transition-colors`}
                                            title="Translate"
                                        >
                                            <span className="material-icons-round text-blue-500">translate</span>
                                        </button>
                                        {!hotel.isApproved && (
                                            <button
                                                onClick={() => handleApprove(hotel._id)}
                                                className={`p-2 rounded-lg ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'} transition-colors`}
                                                title="Approve"
                                            >
                                                <span className="material-icons-round text-green-500">check_circle</span>
                                            </button>
                                        )}
                                        <button
                                            onClick={() => handleDelete(hotel._id)}
                                            className={`p-2 rounded-lg ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'} transition-colors`}
                                            title="Delete"
                                        >
                                            <span className="material-icons-round text-red-500">delete</span>
                                        </button>
                                    </div>
                                </div>

                                {/* Description Preview */}
                                {hotel.descriptions?.original && (
                                    <div className={`mt-4 p-3 rounded-lg ${isDark ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                                        <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'} line-clamp-2`}>
                                            {hotel.descriptions.original}
                                        </p>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="flex justify-center gap-2 mt-6">
                        <button
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            disabled={currentPage === 1}
                            className={`px-4 py-2 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-white'} border ${isDark ? 'border-gray-700' : 'border-gray-300'} disabled:opacity-50`}
                        >
                            Previous
                        </button>
                        <span className={`px-4 py-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                            Page {currentPage} of {totalPages}
                        </span>
                        <button
                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                            disabled={currentPage === totalPages}
                            className={`px-4 py-2 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-white'} border ${isDark ? 'border-gray-700' : 'border-gray-300'} disabled:opacity-50`}
                        >
                            Next
                        </button>
                    </div>
                )}
            </div>

            {/* Detail Modal */}
            {isDetailModalOpen && selectedHotel && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <div className={`w-full max-w-4xl max-h-[90vh] overflow-hidden rounded-2xl ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-2xl`}>
                        {/* Modal Header */}
                        <div className={`p-6 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'} flex items-start justify-between`}>
                            <div>
                                <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                    {selectedHotel.hotelName}
                                </h2>
                                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'} mt-1`}>
                                    {selectedHotel.sourceUrl}
                                </p>
                            </div>
                            <button
                                onClick={() => setIsDetailModalOpen(false)}
                                className={`p-2 rounded-lg ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                            >
                                <span className="material-icons-round">close</span>
                            </button>
                        </div>

                        {/* Modal Content */}
                        <div className="p-6 overflow-y-auto max-h-[calc(90vh-160px)]">
                            {/* Description */}
                            <div className="mb-6">
                                <h3 className={`text-lg font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                    Description
                                </h3>
                                <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
                                    <p className={`${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                        {selectedHotel.descriptions?.original || 'No description available'}
                                    </p>
                                    {selectedHotel.descriptions?.translated?.sv && (
                                        <div className="mt-4 pt-4 border-t border-gray-600">
                                            <p className="text-xs font-medium text-teal-500 mb-2">Swedish Translation</p>
                                            <p className={`${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                                {selectedHotel.descriptions.translated.sv}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Images */}
                            {selectedHotel.images && selectedHotel.images.length > 0 && (
                                <div className="mb-6">
                                    <h3 className={`text-lg font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                        Images ({selectedHotel.images.length})
                                    </h3>
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                        {selectedHotel.images.slice(0, 9).map((img, idx) => (
                                            <img
                                                key={idx}
                                                src={img.url}
                                                alt={img.alt || `Image ${idx + 1}`}
                                                className="w-full h-32 object-cover rounded-lg"
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Room Types */}
                            {selectedHotel.roomTypes && selectedHotel.roomTypes.length > 0 && (
                                <div className="mb-6">
                                    <h3 className={`text-lg font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                        Room Types ({selectedHotel.roomTypes.length})
                                    </h3>
                                    <div className="space-y-3">
                                        {selectedHotel.roomTypes.map((room, idx) => (
                                            <div key={idx} className={`p-4 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
                                                <h4 className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                                    {room.name}
                                                </h4>
                                                {room.description && (
                                                    <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                                        {room.description}
                                                    </p>
                                                )}
                                                {room.amenities && room.amenities.length > 0 && (
                                                    <div className="flex flex-wrap gap-2 mt-2">
                                                        {room.amenities.map((amenity, aidx) => (
                                                            <span key={aidx} className="text-xs px-2 py-1 bg-teal-500/20 text-teal-400 rounded">
                                                                {amenity}
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Contact Info */}
                            {selectedHotel.contactInfo && Object.keys(selectedHotel.contactInfo).length > 0 && (
                                <div className="mb-6">
                                    <h3 className={`text-lg font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                        Contact Information
                                    </h3>
                                    <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
                                        {selectedHotel.contactInfo.phone && (
                                            <p className={`${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                                <strong>Phone:</strong> {selectedHotel.contactInfo.phone}
                                            </p>
                                        )}
                                        {selectedHotel.contactInfo.email && (
                                            <p className={`${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                                <strong>Email:</strong> {selectedHotel.contactInfo.email}
                                            </p>
                                        )}
                                        {selectedHotel.contactInfo.website && (
                                            <p className={`${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                                <strong>Website:</strong> {selectedHotel.contactInfo.website}
                                            </p>
                                        )}
                                        {selectedHotel.contactInfo.address && (
                                            <p className={`${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                                <strong>Address:</strong> {selectedHotel.contactInfo.address}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Modal Footer */}
                        <div className={`p-6 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'} flex justify-end gap-3`}>
                            <button
                                onClick={() => setIsDetailModalOpen(false)}
                                className={`px-4 py-2 rounded-lg ${isDark ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'} transition-colors`}
                            >
                                Close
                            </button>
                            {!selectedHotel.isApproved && (
                                <button
                                    onClick={() => {
                                        handleApprove(selectedHotel._id);
                                        setIsDetailModalOpen(false);
                                    }}
                                    className="px-4 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors"
                                >
                                    Approve Content
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ScrapedHotelsManager;
